import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { countryStateMap } from "../../components/ProjectManagerDashboard/countryStateData";
import ProtectedButton from "../../components/RBAC/ProtectedButton";
import { ApiHelper } from "../../utils/ApiHelper";
import {  UPLOAD_COMPANY_LOGO, COMPANY_DETAIL_OF_USER, UPDATE_COMPANY_DETAIL } from "../../lib/API_KEYS";

export default function UpdateProjectManagerForm() {
  const navigate = useNavigate();

  const initialFormData = {
    fullName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    industry: [],
    otherIndustry: "",
    industryExpertise: "",
    otherIndustryExpertise: "",
    companyName: "",
    experienceYears: "",
    certifications: "",
    linkedIn: "",
    gst: "",
    pna: "",
    experienceBrief: "",
    projectSize: "",
    references: [],
    referenceImage: null,
    confirmCheck: false,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [originalData, setOriginalData] = useState(initialFormData);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const INDUSTRIES = [
    "Automotive",
    "Aerospace",
    "Consumer Goods",
    "Electronics & Communications",
    "Medical Equipment",
    "Industrial Machinery",
    "Others"
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await new ApiHelper().FetchFromServer(
          ...COMPANY_DETAIL_OF_USER
        );
        if (res.data) {
          const data = res.data;

          // Ensure references array exists
          data.references = data.references || [];
          setFormData(data);
          setOriginalData(data);
          // setInitialData(data);
        }
        if (res.data.referenceImage) {
          setUploadedImage({ name: "Uploaded Image", url: res.data.referenceImage });
        }
      } catch (err) {
        console.error("Fetch error:", err);
        // toast.error("Failed to fetch form data");
        showToast("Failed to fetch form data.", "error");
      }
    };
    fetchData();
  }, []);

  const showToast = (msg, type = "success") => {
    toast.dismiss();
    toast[type](msg, {
      toastId: "globalToast",
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "colored",
    });
  };




  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    if (id === "country") {
      setFormData((prev) => ({ ...prev, country: value, state: "" }));
      return;
    }
    setFormData((prev) => ({ ...prev, [id]: type === "checkbox" ? checked : value }));
  };

  const handleReferenceChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.references];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, references: updated };
    });
  };

  const handleAddReference = () => {
    setFormData((prev) => ({
      ...prev,
      references: [...prev.references, { name: "", company: "", email: "", phone: "" }],
    }));
  };

  const handleRemoveReference = (index) => {
    setFormData((prev) => ({
      ...prev,
      references: prev.references.filter((_, i) => i !== index),
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      showToast("File must be <= 2MB.", "error");
      return;
    }
    setUploadedImage(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      showToast("File must be <= 2MB.", "error");
      return;
    }
    setUploadedImage(file);
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
  };


  const isFormChanged = () => {
    const deepEqual = (a, b) => {
      if (a === b) return true;
      if (typeof a !== typeof b) return false;
      if (typeof a === "object" && a !== null && b !== null) {
        if (Array.isArray(a) && Array.isArray(b)) {
          if (a.length !== b.length) return false;
          return a.every((item, index) => deepEqual(item, b[index]));
        }
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        return aKeys.every((key) => deepEqual(a[key], b[key]));
      }
      return false;
    };

    // Compare main form data excluding referenceImage
    for (const key of Object.keys(formData)) {
      if (key === "referenceImage") continue;
      if (!deepEqual(formData[key], originalData[key])) return true;
    }

    // Compare image properly
    const originalImageUrl = originalData.referenceImage || "";
    const currentImageUrl = uploadedImage
      ? uploadedImage instanceof File
        ? "" // user uploaded new file => form changed
        : uploadedImage.url || "" // prefilled image
      : ""; // user removed image

    if (originalImageUrl !== currentImageUrl) return true;

    return false;
  };


  const onBack = () => navigate(-1);

  const onSubmit = async (e) => {
    e.preventDefault();

    const { fullName, email, phone, address, name, industry, linkedIn, confirmCheck, ...rest } = formData;

    if (!confirmCheck) {
      toast.error('Please confirm before submitting.');
      return;
    }

    if (!fullName || !email || !phone || !address || !industry || !name || !linkedIn ) {
      toast.error('Please fill all the required fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      let referenceImage = originalData.referenceImage || "";
      if (uploadedImage && uploadedImage instanceof File) {
        const uploadRes = await new ApiHelper().FetchFromServer(
          ...UPLOAD_COMPANY_LOGO,
          undefined,
          { fileName: uploadedImage.name, fileType: uploadedImage.type, fileSize: uploadedImage.size },
        );


        if (uploadRes.data?.isError || !uploadRes.data?.signedRequest) {
          console.error("Failed to get upload URL");
          return;
        }

        // Upload to S3
        await fetch(uploadRes.data.signedRequest, {
          method: "PUT", body: uploadedImage,
          headers: {
            "Content-Type": uploadedImage.type,
          },
        });
        referenceImage = uploadRes.data.fileUrl;
      }

      const finalData = { ...formData, referenceImage, status: 'In Review', type: 'Project Manager' };

      await new ApiHelper().FetchFromServer(
        ...UPDATE_COMPANY_DETAIL,
        undefined,
        finalData,
      );

      showToast("Data updated successfully!", "success");
      setOriginalData(finalData);
      setUploadedImage(null);
      setTimeout(() => navigate('/manager-dashboard'), 2000);
    } catch (err) {
      showToast('Submission failed.', "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeChip = (item) => {
    setFormData(prev => {
      const updated = prev.industry.filter(i => i !== item);

      return {
        ...prev,
        industry: updated,
        otherIndustry: item === "Others" ? "" : prev.otherIndustry
      };
    });
  };

  const handleSelect = (e) => {
    const value = e.target.value;

    if (!value) return;

    // If selecting Others → only add "Others"
    if (value === "Others") {
      setFormData(prev => ({
        ...prev,
        industry: [...prev.industry, value]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        industry: [...prev.industry.filter(i => i !== "Others"), value],
        otherIndustry: ""
      }));
    }
  };

  if (!formData) return <div>Loading...</div>;

  return (
    <form onSubmit={onSubmit}>
      <div className="container py-5">
        <style>{`.form-control:focus{border-color:#000000;box-shadow:none;outline:none;transition:border-color .3s,box-shadow .3s}`}</style>

        <h4 className="mb-4">Update Project Manager Info</h4>

        {/* Show admin concern if status is Query Raised */}
        {formData.status === "Query Raised" && formData.concern && (
          <div className="alert alert-warning mb-4" role="alert">
            <h6 className="alert-heading">
              <strong>⚠️ Admin Feedback:</strong>
            </h6>
            <p className="mb-0">{formData.concern}</p>
            <hr />
            <small className="text-muted">
              Please address the concerns above and resubmit your application.
            </small>
          </div>
        )}

        <hr />

        <section className="mb-3 mt-4">
          <h6 className="fw-bold">Personal Information</h6>
          <div className="row g-3 mt-3">
            <div className="col-md-6">
              <label className="form-label">Full Name <span style={{ color: "red" }}>*</span></label>
              <input type="text" value={formData.fullName} onChange={handleChange} id="fullName" className="form-control" placeholder="Enter full name" />
            </div>
            <div className="col-md-6">
              <label className="form-label">Email Address <span style={{ color: "red" }}>*</span></label>
              <input type="email" value={formData.email} onChange={handleChange} id="email" className="form-control" placeholder="Enter email" />
            </div>
            <div className="col-md-6">
              <label className="form-label">Phone Number <span style={{ color: "red" }}>*</span></label>
              <input type="text" value={formData.phone} onChange={handleChange} id="phone" className="form-control" placeholder="Enter phone" />
            </div>
            <div className="col-md-6">
              <label className="form-label">Street Address <span style={{ color: "red" }}>*</span></label>
              <input type="text" value={formData.address} onChange={handleChange} id="address" className="form-control" placeholder="Street Address" />
            </div>
            <div className="col-md-6">
              <label className="form-label">City</label>
              <input type="text" value={formData.city} onChange={handleChange} id="city" className="form-control" placeholder="City" />
            </div>
            <div className="col-md-6">
              <label className="form-label">Country</label>
              <select value={formData.country} onChange={handleChange} id="country" className="form-select">
                <option value="">Select country</option>
                <option value="India">India</option>
                <option value="United States">United States</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Australia">Australia</option>
                <option value="Canada">Canada</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">State</label>
              <select value={formData.state} onChange={handleChange} id="state" className="form-select" disabled={!formData.country}>
                <option value="">Select state</option>
                {(countryStateMap[formData.country] || []).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">ZIP/Postal Code</label>
              <input type="text" value={formData.zip} onChange={handleChange} id="zip" className="form-control" placeholder="ZIP code" />
            </div>
            <div className="row g-3 ">
              <div className="col-md-6">
                <div className="d-flex flex-wrap mb-1 gap-2">
                  {formData?.industry?.map(item => (
                    <span className="chip" key={item}>
                      {item}
                      <span className="chip-close" onClick={() => removeChip(item)}>✕</span>
                    </span>
                  ))}
                </div>
                <select className="form-select" onChange={handleSelect}>
                  <option value="">Select Industry <span style={{ color: "red" }}>*</span></option>
                  {formData?.industry && INDUSTRIES.filter(item => !formData?.industry?.includes(item))?.map(item => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>

              {formData.industry.includes("Others") && (
                <div className="col-md-6">
                  <label htmlFor="otherIndustry" className="form-label">Specify Industry</label>
                  <input
                    className="form-control mt-2"
                    placeholder="Specify industry"
                    value={formData.otherIndustry}
                    onChange={(e) => setFormData(prev => ({ ...prev, otherIndustry: e.target.value }))}
                  />
                </div>
              )}

            </div>
            <div className="col-md-6">
              <label className="form-label">Industry Expertise</label>
              <select value={formData.industryExpertise} onChange={handleChange} id="industryExpertise" className="form-select">
                <option value="">Select</option>
                <option value="Precision Machining">Precision Machining</option>
                <option value="Injections Moulds">Injections Moulds</option>
                <option value="Die Casting Components">Die Casting Components</option>
                <option value="Sheet Metal / Stamping">Sheet Metal / Stamping</option>
                <option value="Forging">Forging</option>
                <option value="Sub-assembly">Sub-assembly</option>
                <option value="Aerospace Components">Aerospace Components</option>
                <option value="Engineering & Design Services">Engineering & Design Services</option>
                <option value="Prototyping">Prototyping</option>
                <option value="Other">Other</option>
              </select>
            </div>
            {formData.industryExpertise === "Other" && (
              <div className="col-md-6">
                <label className="form-label">Specify Industry Expertise</label>
                <input type="text" value={formData.otherIndustryExpertise} onChange={handleChange} id="otherIndustryExpertise" className="form-control" placeholder="Specify here" />
              </div>
            )}
          </div>
        </section>

        <hr />


        <section className="mb-3 mt-4">
          <h6 className="fw-bold">Professional Information</h6>
          <div className="row g-3 mt-3">
            <div className="col-md-6">
              <label className="form-label">Company/Organization Name <span style={{ color: "red" }}>*</span></label>
              <input type="text" value={formData.name} onChange={handleChange} id="companyName" className="form-control" placeholder="Company Name" />
            </div>
            <div className="col-md-6">
              <label className="form-label">Years of Experience</label>
              <input type="text" value={formData.experienceYears} onChange={handleChange} id="experienceYears" className="form-control" placeholder="Years of Experience" />
            </div>
            <div className="col-md-6">
              <label className="form-label">Certifications</label>
              <input type="text" value={formData.certifications} onChange={handleChange} id="certifications" className="form-control" placeholder="Certifications" />
            </div>
            <div className="col-md-6">
              <label className="form-label">LinkedIn Profile <span style={{ color: "red" }}>*</span></label>
              <input type="url" value={formData.linkedIn} onChange={handleChange} id="linkedIn" className="form-control" placeholder="LinkedIn URL" />
            </div>
          </div>
        </section>

        <hr />


        <section className="mb-3 mt-4">
          <h6 className="fw-bold">Legal Information</h6>
          <div className="row g-3 mt-2">
            <div className="col-md-6">
              <label className="form-label">GST Number</label>
              <input type="text" value={formData.gst} onChange={handleChange} id="gst" className="form-control" placeholder="GST Number" />
            </div>
            <div className="col-md-6">
              <label className="form-label">PAN Number</label>
              <input type="text" value={formData.pan} onChange={handleChange} id="pan" className="form-control" placeholder="PAN Number" />
            </div>
          </div>
        </section>

        <hr />


        <section className="mb-3 mt-4">
          <h6 className="fw-bold">Project Management Experience</h6>
          <div className="mb-3 mt-3">
            <label className="form-label">Brief Experience</label>
            <textarea value={formData.experienceBrief} onChange={handleChange} id="experienceBrief" className="form-control" rows="3" placeholder="Experience Details" />
          </div>
          <div className="mb-3">
            <label className="form-label">Project Size & Complexity</label>
            <textarea value={formData.projectSize} onChange={handleChange} id="projectSize" className="form-control" rows="3" placeholder="Project Details" />
          </div>
        </section>

        <hr />


        <section className="mb-4 mt-4">
          <h6 className="fw-bold">References</h6>
          {formData?.references?.map((ref, index) => (
            <div className="row g-3 mt-2 align-items-end" key={index}>
              <div className="col-md-3">
                <label className="form-label">Reference {index + 1} Name</label>
                <input type="text" className="form-control" value={ref.name} onChange={(e) => handleReferenceChange(index, "name", e.target.value)} placeholder="Name" />
              </div>
              <div className="col-md-3">
                <label className="form-label">Company/Organization</label>
                <input type="text" className="form-control" value={ref.company} onChange={(e) => handleReferenceChange(index, "company", e.target.value)} placeholder="Company" />
              </div>
              <div className="col-md-3">
                <label className="form-label">Email</label>
                <input type="email" className="form-control" value={ref.email} onChange={(e) => handleReferenceChange(index, "email", e.target.value)} placeholder="Email" />
              </div>
              <div className="col-md-2">
                <label className="form-label">Phone</label>
                <input type="text" className="form-control" value={ref.phone} onChange={(e) => handleReferenceChange(index, "phone", e.target.value)} placeholder="Phone" />
              </div>
              <div className="col-md-1 d-flex justify-content-end">
                <ProtectedButton onClick={() => handleRemoveReference(index)}>
                  <img src="/images/deleteReference.svg" alt="Delete" style={{ cursor: "pointer" }} />
                </ProtectedButton>
              </div>
            </div>
          ))}
          <div className="mt-3">
            <ProtectedButton className="btn btn-outline-dark" onClick={handleAddReference}>
              Add Reference
            </ProtectedButton>
          </div>
        </section>


        <section className="mb-4 p-2" style={{ border: "1px dashed #ccced0ff" }}>
          <div className="rounded p-4 text-center text-muted" style={{ cursor: "pointer", backgroundColor: "#dedfe0ff" }} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
            <input type="file" onChange={handleImageUpload} style={{ display: "none" }} id="uploadInput" />
            <label htmlFor="uploadInput" className="w-100 h-100 p-4">
              <img src="/images/cloud-upload.svg" alt="Upload" />
              <br /> Drop an image here or click to <span className="text-danger fw-bold">upload</span><br />
              PNG, JPG, GIF, ... up to 2MB
            </label>
          </div>

          {uploadedImage && (
            <ul className="list-group mt-3">
              <li className="list-group-item d-flex justify-content-between align-items-center">
                {uploadedImage.name}
                <div>
                  <button type="button" className="btn btn-sm btn-outline-danger me-2" onClick={handleRemoveImage}>Remove</button>
                </div>
              </li>
            </ul>
          )}
        </section>


        <section className="mb-4">
          <div className="form-check">
            <input 
              type="checkbox" 
              className="form-check-input" 
              id="confirmCheck" 
              checked={formData.confirmCheck || false} 
              onChange={handleChange} 
            />
            <label className="form-check-label" htmlFor="confirmCheck">
              By submitting this form, I confirm that the information provided is true and accurate to the best of my knowledge.
            </label>
          </div>
        </section>


        <div className="d-flex justify-content-end gap-3">
          <button type="button" className="btn btn-outline-secondary" onClick={onBack}>Cancel</button>
          <button type="submit" className="btn btn-dark" disabled={!isFormChanged() || isSubmitting}>
            {isSubmitting ? "Updating..." : "Update"}
          </button>
        </div>
      </div>

      <style>{`
        .chip {
          padding: 6px 12px;
          background: #f3f3f3;
          border-radius: 20px;
        }
        .chip-close {
          margin-left: 8px;
          cursor: pointer;
        }
      `}</style>

      <ToastContainer />
    </form>
  );
}
